import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { FilterCategory, FilterOption, FilterOptions } from '../../utils/types';
import { Popup } from '../../../core/services/popup';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-filter',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './filter.html',
  styleUrl: './filter.css'
})
export class Filter {
  @Input() defaultFilter!: FilterOptions;
  @Input() filterOptions!: FilterOptions;

  @Output() apply = new EventEmitter<FilterOptions>();
  @Output() reset = new EventEmitter<void>();

  @ViewChild('filterWrapper') filterWrapper!: ElementRef;

  filterClicked: boolean = false;
  disableApply: boolean = true;
  showReset: boolean = false;
  applied = false;

  constructor(private popup: Popup) { }

  toggleFilter() {
    this.filterClicked = !this.filterClicked;
    if (this.filterClicked) {
      // Recalculate button states when opening
      this.updateState();
    }
  }

  onRadioSelect(category: FilterCategory, option: FilterOption) {
    category.options.forEach(o => (o.checked = false));
    option.checked = true;
    this.updateState();
  }

  toggleCheck(option: FilterOption) {
    option.checked = !option.checked;
    this.updateState();
  }

  updateState() {
    const changed = !this.isDefaultState();
    this.disableApply = !changed;
    this.showReset = changed;
  }

  onApply() {
    this.applied = true;
    this.apply.emit(this.filterOptions);
    this.filterClicked = false;

    this.disableApply = true;
    this.showReset = true;

    this.popup.show({
      message: 'Filter applied',
      type: 'success',
      duration: 5000,
      position: 'bottom',
      icon: 'assets/icons/check-popup.svg',
    });
  }


  onReset() {
    this.filterOptions = JSON.parse(JSON.stringify(this.defaultFilter));
    this.applied = false;  // reset removes applied state
    this.updateState();
    this.reset.emit();
  }


  // CLICK OUTSIDE TO CLOSE
  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (!this.filterWrapper) return;

    if (!this.filterWrapper.nativeElement.contains(event.target)) {
      this.filterClicked = false;
    }
  }

  isDefaultState(): boolean {
    return JSON.stringify(this.filterOptions) === JSON.stringify(this.defaultFilter);
  }

  getCheckedCount(): number {
    let defaultTotal = 0;
    let currentTotal = 0;

    this.filterOptions.forEach(category => {
      if (category.type !== 'check') return;

      // Find corresponding category in defaultFilter
      const defaultCategory = this.defaultFilter.find(c => c.category === category.category);
      if (!defaultCategory) return;

      // Count checked items in default and current filters
      const defaultCount = defaultCategory.options.filter(o => o.checked).length;
      const currentCount = category.options.filter(o => o.checked).length;

      defaultTotal += defaultCount;
      currentTotal += currentCount;
    });

    // Compare totals
    return defaultTotal === currentTotal ? 0 : currentTotal;
  }


  getSelectedSortLabel(): string {
    // Find the sort category by name
    const sortCategory = this.filterOptions.find(c => c.category === 'Sort by');

    if (!sortCategory) return 'Filter';

    // Find selected radio option
    const selected = sortCategory.options.find(o => o.checked);

    return selected ? selected.label : 'Filter';
  }

}
